import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Certificate {
  id: string;
  name: string;
  organization: string;
  project: string;
  date: string;
  tokenId: string;
  status: 'Active' | 'Revoked' | 'Pending' | 'Expired';
  creditAmount: number;
  creditType: string;
  validityPeriod: string;
  verificationURL: string;
  verificationMethod: string;
  serialNumber: string;
  issuer: string;
  description: string;
  location: {
    city: string;
    state: string;
    country: string;
  };
  downloadUrl: string;
  certificateImage: string;
}

interface CertificateState {
  certificates: Certificate[];
  addCertificate: (certificate: Certificate) => void;
  updateCertificate: (id: string, updatedCertificate: Partial<Certificate>) => void;
  deleteCertificate: (id: string) => void;
  getCertificate: (id: string) => Certificate | undefined;
}

// Cria um store persistente usando Zustand para manter os certificados
export const useCertificateStore = create<CertificateState>()(
  persist(
    (set, get) => ({
      certificates: [
        {
          id: "cert-001",
          name: "Certificado Ambiental de Biomassa",
          organization: "Associação Replantar",
          project: "Reflorestamento Urbano",
          date: "2025-01-15",
          tokenId: "TKN78901234",
          status: "Active",
          creditAmount: 350,
          creditType: "VCS",
          validityPeriod: "2025-01-15 a 2030-01-14",
          verificationURL: "https://banclima.com.br/verify/cert-001",
          verificationMethod: "QR Code + Blockchain",
          serialNumber: "BR-VCS-2025-0123",
          issuer: "BanClima Certificadora",
          description: "Certificado de reflorestamento urbano com foco em áreas degradadas na Zona Norte do Rio de Janeiro. Projeto de captura de carbono através de espécies nativas.",
          location: {
            city: "Rio de Janeiro",
            state: "RJ",
            country: "Brasil"
          },
          downloadUrl: "/certificates/certificado-ambiental-biomassa.pdf",
          certificateImage: "/certificates/certificado-biomassa.jpg"
        },
        {
          id: "cert-003",
          name: "Moeda Social de Carbono",
          organization: "Cooperativa ReciclaJá",
          project: "Economia Circular Comunitária",
          date: "2025-03-20",
          tokenId: "TKN12345678",
          status: "Active",
          creditAmount: 125,
          creditType: "SCC",
          validityPeriod: "2025-03-20 a 2028-03-19",
          verificationURL: "https://banclima.com.br/verify/cert-003",
          verificationMethod: "Blockchain",
          serialNumber: "BR-SCC-2025-7890",
          issuer: "BanClima Certificadora",
          description: "Certificado de carbono social emitido para projeto de reciclagem e economia circular em comunidades do Rio de Janeiro, reduzindo 125 tCO2e através de práticas de coleta seletiva e reaproveitamento.",
          location: {
            city: "Rio de Janeiro",
            state: "RJ",
            country: "Brasil"
          },
          downloadUrl: "/certificates/moeda-social-carbono.pdf",
          certificateImage: "/certificates/certificado-social.jpg"
        },
        {
          id: "cert-004",
          name: "Moeda Social Carbono",
          organization: "Associação Verde Futuro",
          project: "Moeda Social Carbono (2025)",
          date: "2025-03-31",
          tokenId: "MSC-001",
          status: "Active",
          creditAmount: 89.08,
          creditType: "CO₂e",
          validityPeriod: "2025-03-31 a 2026-03-30",
          verificationURL: "https://banclima.com.br/verify/cert-004",
          verificationMethod: "Blockchain",
          serialNumber: "BCL-10-2025-001",
          issuer: "BanClima Certificadora",
          description: "Certificado referente ao projeto Moeda Social Carbono, reconhecendo a redução de 89,08 tCO₂e por meio de práticas sustentáveis e economia verde.",
          location: {
            city: "Rio de Janeiro",
            state: "RJ",
            country: "Brasil"
          },
          downloadUrl: "/certificates/moeda-social-carbono.pdf",
          certificateImage: "/certificates/moeda-social-carbono.jpg"
        }
      ],
      
      addCertificate: (certificate) => {
        set((state) => ({
          certificates: [...state.certificates, certificate]
        }));
      },
      
      updateCertificate: (id, updatedCertificate) => {
        set((state) => ({
          certificates: state.certificates.map((cert) => 
            cert.id === id ? { ...cert, ...updatedCertificate } : cert
          )
        }));
      },
      
      deleteCertificate: (id) => {
        set((state) => ({
          certificates: state.certificates.filter((cert) => cert.id !== id)
        }));
      },
      
      getCertificate: (id) => {
        return get().certificates.find((cert) => cert.id === id);
      }
    }),
    {
      name: 'certificate-storage'
    }
  )
);

export default useCertificateStore; 